import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { environment } from 'src/environments/environment';

interface FaqBlock {
  bullets?: string[];
}

interface FaqItem {
  id?: string;
  question: string;
  blocks: (string | FaqBlock)[];
}

interface FaqCategory {
  heading: string;
  items: FaqItem[];
}

interface FaqContent {
  heading: string;
  categories: FaqCategory[];
}

interface RenderedFaqItem {
  id?: string;
  question: string;
  html: SafeHtml;
}

interface RenderedFaqCategory {
  heading: string;
  items: RenderedFaqItem[];
}

@Component({
  selector: 'app-faq',
  standalone: false,
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.css'
})
export class FaqComponent implements OnInit, AfterViewInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);

  year = environment.currentAwardYear;
  heading = 'Frequently Asked Questions';
  categories: RenderedFaqCategory[] = [];
  private fragment: string | null = null;
  expandedId: string | null = null;
  loadError = false;

  ngOnInit() {
    this.http.get<FaqContent>('/content/faq.json').subscribe({
      next: (content) => {
        this.heading = content.heading || this.heading;
        this.categories = (content.categories || []).map((category) => ({
          heading: category.heading,
          items: (category.items || []).map((item) => ({
            id: item.id,
            question: item.question,
            html: this.sanitizer.bypassSecurityTrustHtml(
              this.renderBlocks(item.blocks || [])
            ),
          })),
        }));
        setTimeout(() => {
          this.scrollToFragment(this.fragment);
        });
      },
      error: () => {
        this.loadError = true;
      },
    });

    this.route.fragment.subscribe((fragment) => {
      this.fragment = fragment;
      this.scrollToFragment(this.fragment);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.scrollToFragment(this.fragment);
    });
  }

  onOpened(id?: string) {
    if (!id) {
      return;
    }
    this.expandedId = id;
    if (this.fragment === id) {
      this.scrollToFragment(id);
    }
  }

  onClosed(id?: string) {
    if (id && this.expandedId === id) {
      this.expandedId = null;
    }
    if (id && this.fragment === id) {
      this.fragment = null;
    }
  }

  onContentClick(event: MouseEvent) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const anchor = target.closest('a');
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    const href = anchor.getAttribute('href') || '';
    if (href.startsWith('#')) {
      event.preventDefault();
      this.scrollToFragment(decodeURIComponent(href.slice(1)));
      return;
    }

    const portalPrefix = 'https://voters.terpeca.com';
    if (href.startsWith(portalPrefix)) {
      event.preventDefault();
      const path = href.slice(portalPrefix.length) || '/';
      this.router.navigateByUrl(path);
    }
  }

  scrollToFragment(fragment: string | null) {
    this.expandedId = null;
    if (!fragment) {
      return;
    }
    this.expandedId = fragment;
    this.fragment = fragment;
    setTimeout(() => {
      const element = document.querySelector('#' + CSS.escape(fragment));
      if (element) {
        element.scrollIntoView();
        window.scrollBy(0, -56);
      }
    });
  }

  private renderBlocks(blocks: (string | FaqBlock)[]): string {
    return blocks.map((block) => {
      if (typeof block === 'string') {
        return `<p>${this.formatInline(this.withYear(block))}</p>`;
      }
      if (block?.bullets?.length) {
        const items = block.bullets
          .map((item) => `<li>${this.formatInline(this.withYear(item))}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }
      return '';
    }).join('');
  }

  private withYear(text: string): string {
    return text
      .replaceAll('{yearMinus1}', String(this.year - 1))
      .replaceAll('{year}', String(this.year));
  }

  private escapeHtml(text: string): string {
    return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private formatInline(text: string): string {
    let html = this.escapeHtml(text);
    html = html.replace(/\*\*(?<bold>.+?)\*\*/g, '<b>$<bold></b>');
    html = html.replace(/_(?<em>.+?)_/g, '<i>$<em></i>');
    const linkRe = /\[(?<label>[^\]]+)\]\((?<href>[^)]+)\)(?:\{external\})?/g;
    let result = '';
    let lastIndex = 0;
    for (const match of html.matchAll(linkRe)) {
      result += html.slice(lastIndex, match.index);
      const label = match.groups?.label ?? '';
      const href = match.groups?.href ?? '';
      const external = /^(?:https?:\/\/|\/\/)/i.test(href);
      const attrs = external ? ' target="_blank" rel="noopener"' : '';
      result += `<a href="${href}"${attrs}>${label}</a>`;
      lastIndex = (match.index ?? 0) + match[0].length;
    }
    return result + html.slice(lastIndex);
  }
}
